<template>
  <div>
    <NavabrSaas />
    <section class="bg-half-170 d-table w-100 bg-light">
      <div class="container">
        <div class="row mt-5 justify-content-center">
          <div class="col-lg-12 text-center">
            <div class="pages-heading">
              <h4 class="title mb-0">Privacy Policy</h4>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-9">
            <div class="card shadow rounded border-0">
              <div class="card-body">
                <h5 class="card-title">Effective Date: {{ effectiveDate }}</h5>
                <h5 class="card-title">Last Updated: {{ lastUpdated }}</h5>
                <p>Applies To: TafiCloud (the "Service") operated by TafiCloud ("we", "us", "our")</p>
                
                <h4 class="mt-4">1. Introduction</h4>
                <p>At TafiCloud, we respect and value your privacy. This Privacy Policy outlines how we collect, use, disclose, and protect your personal information and data when you use our platform, SDKs, AI tools, APIs, and related services. We are committed to transparency and strive to give users control over their data in accordance with international privacy laws including the General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), and others.</p>
                
                <h4 class="mt-4">2. Scope of Policy</h4>
                <p>This Privacy Policy applies to:</p>
                <ul>
                  <li>Users of our website and dashboards</li>
                  <li>Developers integrating our SDKs or APIs</li>
                  <li>Clients using our AI-based features</li>
                  <li>End-users whose data may be processed through client applications</li>
                </ul>
                <p>By using our services, you acknowledge that you've read and understood this policy.</p>
                
                <h4 class="mt-4">3. Information We Collect</h4>
                <h5 class="mt-3">3.1 Personal Information</h5>
                <p>When you create an account or interact with our platform, we may collect:</p>
                <ul>
                  <li>Full name, email address, contact number</li>
                  <li>Company name and billing details</li>
                  <li>Authentication credentials (hashed passwords or OAuth tokens)</li>
                </ul>
                
                <h5 class="mt-3">3.2 Usage and Device Information</h5>
                <p>We automatically collect:</p>
                <ul>
                  <li>IP address and device/browser details</li>
                  <li>Time zone, language settings, and location inferred from IP</li>
                  <li>Operating system and SDK version used</li>
                  <li>File access, upload/download history</li>
                  <li>Platform navigation and feature usage metrics</li>
                </ul>
                
                <h5 class="mt-3">3.3 File and Content Data</h5>
                <p>You may upload documents, images, videos, and other file types. We store and process:</p>
                <ul>
                  <li>File metadata (name, type, size, creation date)</li>
                  <li>AI-generated insights (tags, summaries, embeddings, categorization)</li>
                  <li>Associated logs such as time of upload, user action, and source</li>
                </ul>
                <p>Note: Content is encrypted at rest and during transit.</p>
                
                <h5 class="mt-3">3.4 Cookies and Tracking Technologies</h5>
                <p>We use cookies and similar technologies for:</p>
                <ul>
                  <li>Session management and authentication</li>
                  <li>Platform performance and personalization</li>
                  <li>Analytics and diagnostics</li>
                </ul>
                <p>You can manage cookie preferences through your browser settings.</p>
                
                <h4 class="mt-4">4. How We Use Your Information</h4>
                <p>We use your information to:</p>
                <ul>
                  <li>Provide core storage and data access services</li>
                  <li>Enable AI-driven features like natural language search, tagging, and recommendations</li>
                  <li>Deliver developer tools and API/SDK integrations</li>
                  <li>Process automation and notifications via workflows</li>
                  <li>Ensure platform security, uptime, and usage insights</li>
                  <li>Communicate account activity, billing, or product updates</li>
                  <li>Enforce our Terms of Service and prevent abuse or fraud</li>
                </ul>
                
                <h4 class="mt-4">5. AI-Specific Processing</h4>
                <p>We process file content to provide advanced features such as:</p>
                <ul>
                  <li>Natural Language Search</li>
                  <li>Auto-categorization and Smart Tagging</li>
                  <li>Related file discovery and summarization</li>
                </ul>
                <p>Our models are trained on large-scale, anonymized datasets. Uploaded content is used only to deliver your selected features, not for training general-purpose models unless explicitly permitted.</p>
                
                <h4 class="mt-4">6. Legal Basis for Processing (for EEA Users)</h4>
                <p>If you are located in the European Economic Area (EEA), we process your data based on:</p>
                <ul>
                  <li>Consent (e.g., marketing communications, cookies)</li>
                  <li>Contractual necessity (e.g., account creation, SDK use)</li>
                  <li>Legal obligations (e.g., tax compliance)</li>
                  <li>Legitimate interest (e.g., improving features, ensuring security)</li>
                </ul>
                
                <h4 class="mt-4">7. Data Sharing & Disclosure</h4>
                <p>We do not sell your personal data. We only share it in limited cases:</p>
                <ul>
                  <li>Service Providers: Hosting, analytics, email delivery, etc., under strict confidentiality agreements</li>
                  <li>Legal Requirements: If required by law, court orders, or law enforcement</li>
                  <li>Business Transfers: As part of a merger, acquisition, or asset sale, with prior notice</li>
                  <li>With Your Consent: When you integrate third-party services or opt into data sharing</li>
                </ul>
                
                <h4 class="mt-4">8. Data Security</h4>
                <p>We take your data protection seriously. Measures include:</p>
                <ul>
                  <li>AES-256 encryption at rest and TLS 1.2+ in transit</li>
                  <li>Role-based access control and audit logging</li>
                  <li>Regular security assessments and vulnerability scans</li>
                  <li>Secure backup and disaster recovery processes</li>
                  <li>Isolated AI processing environments for sensitive content</li>
                </ul>
                
                <h4 class="mt-4">9. Data Retention</h4>
                <p>We retain data based on the following:</p>
                <ul>
                  <li>Active account content: As long as your account is active or until you delete it</li>
                  <li>Deleted files: Retained for 30 days by default (unless purged earlier)</li>
                  <li>Logs and analytics: Retained for a maximum of 12 months</li>
                  <li>Billing and legal data: Retained for up to 7 years as required by financial laws</li>
                </ul>
                <p>You may request data deletion at any time (see Section 10).</p>
                
                <h4 class="mt-4">10. Your Rights and Choices</h4>
                <p>Depending on your location, you may have the following rights:</p>
                
                <h5 class="mt-3">10.1 Access & Portability</h5>
                <p>You may request a copy of your data in a structured, machine-readable format.</p>
                
                <h5 class="mt-3">10.2 Correction</h5>
                <p>You can update or correct your information from your dashboard.</p>
                
                <h5 class="mt-3">10.3 Deletion</h5>
                <p>You may request permanent deletion of your personal data and associated files.</p>
                
                <h5 class="mt-3">10.4 Objection or Restriction</h5>
                <p>You may object to certain types of processing or limit data usage (e.g., AI summaries or analytics).</p>
                
                <h5 class="mt-3">10.5 Withdraw Consent</h5>
                <p>You may unsubscribe from emails and revoke consent for non-essential cookies or tracking.</p>
                
                <p>To exercise your rights, contact us at <a href="mailto:privacy@taficloud.com">privacy@taficloud.com</a></p>
                
                <h4 class="mt-4">11. International Transfers</h4>
                <p>Our servers and partners may be located in multiple jurisdictions. Where applicable, we implement:</p>
                <ul>
                  <li>Standard Contractual Clauses (SCCs)</li>
                  <li>Data Processing Agreements (DPAs)</li>
                  <li>Privacy Shield or equivalent frameworks</li>
                </ul>
                <p>We ensure adequate safeguards for cross-border data flow.</p>
                
                <h4 class="mt-4">12. Children's Privacy</h4>
                <p>Our platform is intended for professional and commercial use. We do not knowingly collect data from children under 13 (or applicable age in your region). If we become aware, we will delete such data promptly.</p>
                
                <h4 class="mt-4">13. Third-Party Services</h4>
                <p>Some features (e.g., CDN delivery, email automation, payment) may use third-party providers. Their use is governed by their respective privacy policies, which we ensure are compliant with privacy regulations.</p>
                
                <h4 class="mt-4">14. Changes to This Privacy Policy</h4>
                <p>We may update this policy from time to time. If significant changes are made:</p>
                <ul>
                  <li>You'll be notified via email or platform alerts</li>
                  <li>We'll update the effective date at the top of the policy</li>
                </ul>
                <p>We recommend checking this page periodically.</p>
                
                <h4 class="mt-4">15. Contact Us</h4>
                <p>For questions, requests, or complaints regarding this Privacy Policy:</p>
                <p>
                  TafiCloud<br>
                  Attn: Privacy Officer<br>
                  Email: <a href="mailto:privacy@taficloud.com">privacy@taficloud.com</a><br>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <FooterTafiCloud />
  </div>
</template>

<script>
import NavabrSaas from '@/components/Navbar/Navabr-saas.vue';
import FooterTafiCloud from '@/components/Footer/Footer-TafiCloud.vue';

export default {
  name: "PrivacyPolicy",
  components: {
    NavabrSaas,
    FooterTafiCloud
  },
  data() {
    return {
      effectiveDate: "June 1, 2024",
      lastUpdated: "June 1, 2024"
    }
  }
}
</script>

<style lang="scss" scoped>
.section {
  padding-top: 60px;
}

h4 {
  color: #333;
  font-weight: 600;
}

h5 {
  color: #555;
  font-weight: 500;
}

ul {
  margin-bottom: 1rem;
}

.card {
  border-radius: 10px;
}

.card-body {
  padding: 2rem;
}
</style>